import * as FC from './form-condition';
import { isCompareEquals } from '../conditions/evaluate-comparison';
export const DEFAULT_VALIDATE_PROMPT = `Call \`validate_intake\`.

If validation returns errors:
- If you already have the correct data to fix the issue, save that block again with the corrected data. Do not ask the caller unless you need new information.
- If you are missing data, ask the caller for it. Then save that specific block with all the previous data AND the newly gathered data included.
- After fixing, call \`validate_intake\` again. Repeat until validation passes.

This step is crucial — do not proceed until validation is fully complete.`;
export const DEFAULT_BLOCK_START_TEXT = 'For each field in this block: first check if the caller already mentioned it. If yes, use what they said. If no, ask for it.';
const formatFieldTypeHint = (field) => {
    if (field.type === 'boolean')
        return ' (yes/no)';
    if (field.type === 'options')
        return ' (select from options)';
    if (field.type === 'list') {
        if (field.items?.type === 'object' && field.items.properties?.length) {
            const subFields = field.items.properties.map((p) => p.name).join(', ');
            return ` (array of: ${subFields})`;
        }
        if (field.items?.type === 'options')
            return ' (select all that apply from options)';
        return ' (array)';
    }
    if (field.type === 'object' && field.properties?.length) {
        const subFields = field.properties.map((p) => p.name).join(', ');
        return ` (object: ${subFields})`;
    }
    if (field.type === 'address')
        return ' (address)';
    if (field.type === 'simpleDate')
        return ' (date, YYYY-MM-DD)';
    if (field.type === 'simpleTime')
        return ' (time, HH:MM AM/PM — deduce AM/PM from context, never ask)';
    if (field.type === 'date')
        return ' (date)';
    if (field.type === 'datetime')
        return ' (date and time)';
    if (field.type === 'email')
        return ' (email)';
    if (field.type === 'phone')
        return ' (phone)';
    if (field.type === 'phoneWithExtension')
        return ' (phone with optional extension)';
    if (field.type === 'ssn')
        return ' (Social Security Number)';
    return '';
};
const generateFieldLine = (field) => {
    if (field.readonly) {
        const desc = field.description ? ` (${field.description})` : '';
        return `Store ${field.name}${desc} silently — do not ask the caller for this.`;
    }
    const typeHint = formatFieldTypeHint(field);
    const desc = field.description ? ` — ${field.description}` : '';
    const req = field.required || field.requiredWhen ? '' : ' (if available)';
    return `${field.name}${typeHint}${desc}${req}.`;
};
const humanizeTitle = (title) => {
    if (title.includes(' '))
        return title;
    return title
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase())
        .trim();
};
const formatComparisonNaturalLanguage = (c) => {
    const name = c.fieldName ? humanizeTitle(c.fieldName) : 'field';
    const isDateCoercion = c.coercion === 'days';
    const opWord = isDateCoercion
        ? {
            gt: 'is after',
            gte: 'is on or after',
            lt: 'is before',
            lte: 'is on or before',
            equals: 'is on',
            not_equals: 'is not on'
        }
        : {
            gt: 'is greater than',
            gte: 'is at least',
            lt: 'is less than',
            lte: 'is at most',
            equals: 'is',
            not_equals: 'is not'
        };
    let target;
    if (c.valueSource === 'today') {
        target =
            c.offset && c.offset !== 0
                ? `${Math.abs(c.offset)} days ${c.offset > 0 ? 'from' : 'before'} today`
                : 'today';
    }
    else if (c.valueSource === 'field') {
        target = humanizeTitle(String(c.expectedValue));
        if (c.offset && c.offset !== 0)
            target += ` ${c.offset > 0 ? '+' : '−'} ${Math.abs(c.offset)}${isDateCoercion ? ' days' : ''}`;
    }
    else {
        target = String(c.expectedValue);
        if (c.offset && c.offset !== 0)
            target += ` ${c.offset > 0 ? '+' : '−'} ${Math.abs(c.offset)}${isDateCoercion ? ' days' : ''}`;
    }
    return `${name} ${opWord[c.operation]} ${target}`;
};
const formatLeaf = (c) => {
    const name = c.fieldName ? humanizeTitle(c.fieldName) : 'field';
    switch (c.operation) {
        case 'gt':
        case 'gte':
        case 'lt':
        case 'lte':
            return formatComparisonNaturalLanguage(c);
        case 'inlist':
            return `${name} is one of: {${c.expectedValues.join(', ')}}`;
        case 'not_inlist':
            return `${name} is not one of: {${c.expectedValues.join(', ')}}`;
        case 'has_any_of':
            return `${name} has any of: {${c.expectedValues.join(', ')}}`;
        case 'has_none_of':
            return `${name} has none of: {${c.expectedValues.join(', ')}}`;
        case 'equals':
        case 'not_equals': {
            if (isCompareEquals(c))
                return formatComparisonNaturalLanguage(c);
            const v = c.expectedValue;
            if (typeof v === 'boolean') {
                const isPositive = (c.operation === 'equals' && v) || (c.operation === 'not_equals' && !v);
                return `${name} is ${isPositive ? 'true' : 'false'}`;
            }
            return c.operation === 'equals' ? `${name} is ${String(v)}` : `${name} is not ${String(v)}`;
        }
        case 'answered':
            return `${name} is answered`;
        case 'not_answered':
            return `${name} is not answered`;
        case 'begins_with':
        case 'not_begins_with':
        case 'ends_with':
        case 'not_ends_with':
            return FC.formatTextMatch(c, name);
    }
};
const formatCondition = (condition) => {
    const normalized = FC.normalize(condition);
    if (!normalized)
        return 'baseline, always ask';
    const formatted = FC.format(normalized, formatLeaf);
    return `only if ${formatted}`;
};
// Every save delivers a block's read-aloud messages, so the agent only needs to know not to say them itself
const readAloudLine = (block) => block.readAloudMessages?.length
    ? 'When you save this block, a message is said to the caller automatically. Do not say it or anything about it.\n'
    : '';
const toLine = (text) => (text?.trim() ? `${text}\n` : '');
const indentLines = (text) => text
    .split('\n')
    .map((line) => (line ? `\t${line}` : line))
    .join('\n');
const generateBlockPrompts = (blocks, { blockStartText = DEFAULT_BLOCK_START_TEXT, blockEndText }) => {
    return blocks
        .map((block, index) => {
        const blockNum = index + 1;
        const isConditional = !!block.condition;
        const tag = isConditional ? 'conditional' : 'required';
        const conditionNote = formatCondition(block.condition);
        const title = humanizeTitle(block.title);
        const fieldParamList = block.fields.map((f) => f.name).join(', ');
        const toolCallName = getBlockToolCallName(block.title);
        let body = '';
        if (block.instructions) {
            body += `${toLine(blockStartText)}${block.instructions}\n${toLine(blockEndText)}`;
        }
        else {
            const readonlyFields = block.fields.filter((f) => f.readonly);
            const askedFields = block.fields.filter((f) => !f.readonly);
            if (readonlyFields.length > 0) {
                body += readonlyFields.map(generateFieldLine).join('\n');
                body += '\n';
            }
            if (askedFields.length > 0) {
                body += askedFields.map(generateFieldLine).join('\n');
                body += '\n';
            }
        }
        body += readAloudLine(block);
        body += `After getting all information, save the data by calling ${toolCallName} with (${fieldParamList}).`;
        return `<block ${blockNum}. [${tag}] ${title} (${conditionNote})>\n${indentLines(body)}\n</block>\n`;
    })
        .join('\n')
        .trimEnd();
};
export const MAX_BLOCK_TITLE_LENGTH = 64 - 'Save_'.length - '_info'.length;
export const BLOCK_TITLE_INVALID_CHAR_RE = /[^a-zA-Z0-9 -]/;
export const getBlockToolCallName = (blockTitle) => `Save_${blockTitle.replace(/\s+/g, '_')}_info`;
export const generateIntakeFormPrompt = ({ blocks = [], advancedNodeInstructions, blockStartText, blockEndText }) => {
    let prompt = '';
    if (advancedNodeInstructions?.trim()) {
        prompt += `${advancedNodeInstructions.trim()}\n\n`;
    }
    prompt += `<INFORMATION_GATHERING>\n${generateBlockPrompts(blocks, { blockStartText, blockEndText })}\n</INFORMATION_GATHERING>`;
    return prompt;
};
//# sourceMappingURL=generate-prompt.js.map